import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import Chart from 'react-apexcharts';
import { themeColors } from '../../../styles/theme';
import { useSelector, useDispatch } from "react-redux";
import StethoscopeLoading from '../../../components/StethoscopeLoading/StethoscopeLoading';
import {
  getUserRiskAnalysisData,
  extractRiskCalculatorData,
  profileUsersDetailsGet
} from 'apiServices';
// import './bmi.css';

const RiskCalculator = (props) => {
  const { register, handleSubmit, reset, formState, setValue: setFormValue, getValues } = useForm({
    mode: 'onChange',
  });

  const [bmiValue, setBmiValue] = useState(null);
  const [bmiResult, setBmiResult] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isShownResults, setIsShownResults] = useState(false);
  // setChatResult
  const [htmlResponse, setHtmlResponse] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isFormValid, setIsFormValid] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.apiService.login);
  const selectedUserId = props.selectedFamilyMember?.user_id || userData.user_id;
  const token = userData?.Token || '';
  const params = { token, user_id: selectedUserId };
  const [chartOptions, setChartOptions] = useState({});
  const [chartSeries, setChartSeries] = useState([0]);
  const { t } = useTranslation();

  const [riskForm, setRiskForm] = useState({
    age: '',
    height: '',
    weight: '',
    systolicBp: '',
    diastolicBp: '',
    totalCholesterol: '',
    hdl: '',
    ldl: '',
    sex: '', // No default - will be set from user data
    on_diabetes_medication: false, // Default not selected
    on_hypertension_medication: false, // Default not selected
    smoker: 'never', // Default selected
    race: 'white', // Default selected
    on_statin: false, // Default not selected
    on_aspirin: false, // Default not selected
  });

  const [value, setValue] = useState(10);
  const series = [value, 40 - value];

  const options = {
    chart: { type: 'donut' },
    labels: ['Used', 'Remaining'],
    plotOptions: {
      pie: { startAngle: -90, endAngle: 90, offsetY: 10, donut: { size: '70%' } },
    },
    dataLabels: { enabled: false },
    legend: { position: 'bottom' },
    tooltip: { enabled: false },
    colors: [themeColors.primary, '#D3D3D3'],
    states: {
      normal: { filter: { type: 'none' } },
      hover: { filter: { type: 'none' } },
      active: { filter: { type: 'none' } },
    },
  };

  const fieldValidations = {
    age: { required: true, min: 20, max: 79, valueAsNumber: true },
    height: { required: true, min: 50, max: 250, valueAsNumber: true },
    weight: { required: true, min: 20, max: 200, valueAsNumber: true },
    systolicBp: { required: true, min: 90, max: 200, valueAsNumber: true },
    diastolicBp: { required: true, min: 60, max: 130, valueAsNumber: true },
    totalCholesterol: { required: true, min: 130, max: 320, valueAsNumber: true },
    hdl: { required: true, min: 20, max: 100, valueAsNumber: true },
    ldl: { required: true, min: 30, max: 300, valueAsNumber: true },
  };

  // Function to validate individual field
  const validateField = (name, value) => {
    const validation = fieldValidations[name];
    if (!validation) return null;

    const numValue = parseFloat(value);

    if (!value || value.toString().trim() === '') {
      return 'empty'; // Just show red border, no message
    }

    if (isNaN(numValue)) {
      return 'empty'; // Just show red border, no message
    }

    if (validation.min && validation.max && (numValue < validation.min || numValue > validation.max)) {
      // Special message for age field
      if (name === 'age') {
        return `Age must be between ${validation.min}-${validation.max}`;
      }
      return `Value must be between ${validation.min}-${validation.max}`;
    }

    return null;
  };

  // Function to validate if all required fields are filled
  const validateForm = () => {
    const requiredFields = ['age', 'height', 'weight', 'systolicBp', 'diastolicBp', 'totalCholesterol', 'hdl', 'ldl'];
    const isValid = requiredFields.every(field => {
      const value = riskForm[field];
      return value && value.toString().trim() !== '';
    });
    setIsFormValid(isValid);
  };

  // Function to validate all fields and update errors
  const validateAllFields = () => {
    const errors = {};
    const requiredFields = ['age', 'height', 'weight', 'systolicBp', 'diastolicBp', 'totalCholesterol', 'hdl', 'ldl'];

    requiredFields.forEach(field => {
      const error = validateField(field, riskForm[field]);
      if (error) {
        errors[field] = error;
      }
    });

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // UseEffect to validate form whenever riskForm changes
  useEffect(() => {
    validateForm();
  }, [riskForm]);

  const calculateHealthRisk = (data) => {
    setIsLoading(true);
    const formData = { ...data, ...riskForm };
    setTimeout(() => {
      setBmiValue(25);
      setBmiResult('Normal');
      setHtmlResponse(data.htmlResponse);
      setIsShownResults(true);
      setIsLoading(false);
    }, 2000);
  };

  const handleToggle = (name, value) => {
    setRiskForm(prev => ({ ...prev, [name]: value }));
  };

  const handleInputChangeAndResetBMI = (name, value) => {
    setRiskForm(prev => ({ ...prev, [name]: value }));

    // Validate the field and update errors
    const error = validateField(name, value);
    setFieldErrors(prev => ({
      ...prev,
      [name]: error
    }));

    if (name === 'height' || name === 'weight' || name === 'age') {
      setBmiValue(0);
      setBmiResult('');
      setChartSeries([0]);
      setIsShownResults(false); // <-- Add this line!
    }
  };

  const handleCalculateRisk = () => {
    // Validate all fields first
    const isAllFieldsValid = validateAllFields();

    if (!isAllFieldsValid) {
      // setErrorMessage('Please fix the validation errors before calculating risk.');
      return;
    }

    const formData = {
      ...riskForm,
      age: getValues('age'),
      height: getValues('height'),
      weight: getValues('weight'),
      systolicBp: getValues('systolicBp'),
      diastolicBp: getValues('diastolicBp'),
      totalCholesterol: getValues('totalCholesterol'),
      hdl: getValues('hdl'),
      ldl: getValues('ldl'),
      token: token,
      userId: selectedUserId
    };
    setIsLoading(true);
    setErrorMessage(''); // Clear any previous error messages
    dispatch(extractRiskCalculatorData(formData))
      .then((response) => {
        if (response.payload) {
          const data = response.payload;
          setHtmlResponse(data);
          // Calculate BMI and update chart here
          const weight = parseFloat(formData.weight);
          const height = parseFloat(formData.height);
          if (weight && height) {
            const bmi = +(weight / ((height / 100) ** 2)).toFixed(2);
            setBmiValue(bmi);
            const result = getBmiCategory(bmi);
            setBmiResult(result);
            const percentageValue = (bmi / MAX_BMI) * 100;
            setChartSeries([percentageValue]);
            prepareBMICHart(bmi, result);
          }
          setValue(data.riskScore || 10);
          setIsShownResults(true);
        }
      })
      .catch((error) => {
        setErrorMessage('Failed to calculate risk. Please try again.');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const resetForm = () => {
    reset({
      age: '',
      height: '',
      weight: '',
      systolicBp: '',
      diastolicBp: '',
      totalCholesterol: '',
      hdl: '',
      ldl: ''
    });
    setRiskForm({
      age: '',
      height: '',
      weight: '',
      systolicBp: '',
      diastolicBp: '',
      totalCholesterol: '',
      hdl: '',
      ldl: '',
      sex: '',
      on_diabetes_medication: false,
      on_hypertension_medication: false,
      smoker: 'never',
      race: 'white',
      on_statin: false,
      on_aspirin: false
    });
    setBmiValue(null);
    setBmiResult('');
    setIsShownResults(false);
    setHtmlResponse('');
    setIsFormValid(false);
    setFieldErrors({});
    setErrorMessage('');
  };

  // Define row colors for consistent styling
  const rowColors = {
    row1: '#ff0066',
    row2: '#0099cc',
    row3: '#cc00ff',
  };

  const renderInputField = (name, labelKey, placeholder, rowColor = 'var(--pmr-text-muted)') => {
    const validation = fieldValidations[name];
    const showRequiredAsterisk = validation?.required;
    const rangeText = validation?.min && validation?.max ? ` (${validation.min}-${validation.max})` : '';

    return (
      <div className="form-group">
        <label className='bmiLabels' htmlFor={name}>
          {t(labelKey)}
          {rangeText && (
            <span style={{ color: rowColor, fontWeight: '600', fontSize: '0.9em' }}>
              {rangeText}
            </span>
          )}
          {showRequiredAsterisk && <span style={{ color: '#dc3545' }}>*</span>}
        </label>
        <input
          type="text"
          className={`form-control ${fieldErrors[name] ? 'is-invalid' : ''}`}
          id={name}
          value={riskForm[name]}
          placeholder={placeholder}
          {...register(name, fieldValidations[name])}
          onChange={(e) => {
            // Allow only numeric characters
            const value = e.target.value.replace(/[^0-9]/g, '');
            handleInputChangeAndResetBMI(name, value);
          }}
          style={{
            borderColor: fieldErrors[name] ? '#dc3545' : '',
            borderWidth: fieldErrors[name] ? '2px' : '1px'
          }}
        />
        {/* {validation?.min && validation?.max && (
          <div className="text-muted" style={{ fontSize: '0.75rem', marginTop: '0.25rem', fontStyle: 'italic' }}>
            {name === 'age' ? `Age must be between ${validation.min}-${validation.max}` : `Value must be between ${validation.min}-${validation.max}`}
          </div>
        )} */}
        {fieldErrors[name] && fieldErrors[name] !== 'empty' && (
          <div className="invalid-feedback d-block" style={{ color: '#dc3545', fontSize: '0.875rem', marginTop: '0.25rem' }}>
            {fieldErrors[name]}
          </div>
        )}
      </div>
    );
  };

  const getBmiCategory = (bmi) => {
    if (bmi < 18.5) return 'Underweight';
    if (bmi < 25) return 'Normal';
    if (bmi < 30) return 'Overweight';
    return 'Obese';
  };

  const getBmiColorStops = (category) => {
    switch (category) {
      case 'Underweight':
        return [0, 25];
      case 'Normal':
        return [25, 50];
      case 'Overweight':
        return [50, 75];
      case 'Obese':
        return [75, 100];
      default:
        return [0, 100];
    }
  };

  const prepareBMICHart = (bmi, result) => {
    const stops = getBmiColorStops(result);
    const percentageValue = (bmi / MAX_BMI) * 100;

    setChartOptions({
      chart: {
        type: 'radialBar',
        offsetY: 10,
        height: 300,
        width: 300
      },
      plotOptions: {
        radialBar: {
          startAngle: -90,
          endAngle: 90,
          track: {
            background: '#e7e7e7',
            strokeWidth: '90%',
            margin: 0,
            dropShadow: {
              enabled: false
            }
          },
          dataLabels: {
            name: {
              show: false
            },
            value: {
              offsetY: 5,
              fontSize: '18px',
              formatter: function (val) {
                return ` ${(val / 100 * MAX_BMI).toFixed(1)} Kgs/m²`;
              }
            }
          }
        }
      },
      fill: {
        type: 'gradient',
        gradient: {
          shade: 'light',
          shadeIntensity: 0.4,
          inverseColors: false,
          opacityFrom: 1,
          opacityTo: 1,
          stops: stops
        }
      },
      labels: ['BMI']
    });
  };

  // useEffect(() => {
  //   const weight = parseFloat(riskForm.weight);
  //   const height = parseFloat(riskForm.height);

  //   if (weight && height) {
  //     const heightInMeters = height / 100;
  //     const bmi = +(weight / (heightInMeters * heightInMeters)).toFixed(2);
  //     setBmiValue(bmi);

  //     const result = getBmiCategory(bmi);
  //     setBmiResult(result);

  //     const percentageValue = (bmi / MAX_BMI) * 100;

  //     setChartSeries([percentageValue]);
  //     prepareBMICHart(bmi, result);
  //   }
  // }, [riskForm.weight, riskForm.height]);
  // Add BMI categories data
  const bmiCategories = [
    { category: 'Underweight', range: '< 18.5' },
    { category: 'Severe underweight', range: '<16.00' },
    { category: 'Moderate underweight', range: '16.00-16.90' },
    { category: 'Mild underweight', range: '17.0-18.49' },
    { category: 'Normal range', range: '18.5-24.9' },
    { category: 'Overweight', range: '≥25.00' },
    { category: 'Pre-obese', range: '25.00-29.90' },
    { category: 'Obesity', range: '≥30.00' },
    { category: 'Obese class I', range: '30.00-34.90' },
    { category: 'Obese class II', range: '35.00-39.90' },
    { category: 'Obese class III', range: '≥40.00' }
  ];

  useEffect(() => {
    // First fetch profile data for gender, then risk analysis data
    dispatch(profileUsersDetailsGet(params))
      .then((profileResponse) => {
        console.log(profileResponse, "profile response");
        let profileGender = '';

        if (profileResponse.payload && profileResponse.payload.Table && profileResponse.payload.Table.length > 0) {
          const userDetails = profileResponse.payload.Table[0];
          // Set gender from profile data if available
          if (userDetails.gender !== undefined && userDetails.gender !== null) {
            profileGender = userDetails.gender.toString() === '0' ? 'male' :
              userDetails.gender.toString() === '1' ? 'female' :
                userDetails.gender.toString() === '2' ? 'other' : '';
            console.log('Setting gender from profile:', profileGender, 'from value:', userDetails.gender);
          }
        }

        // Then fetch risk analysis data
        return dispatch(getUserRiskAnalysisData(params))
          .then((response) => {
            if (response.payload?.Table?.[0]) {
              const data = response.payload.Table[0];

              // Convert all values to string for form fields
              setFormValue('age', data.Age ? String(data.Age) : '');
              setFormValue('height', data.Height ? String(data.Height) : '');
              setFormValue('weight', data.Weight ? String(data.Weight) : '');
              setFormValue('systolicBp', data.Systolic_Blood_Pressure ? String(data.Systolic_Blood_Pressure) : '');
              setFormValue('diastolicBp', data.Diastolic_Blood_Pressure ? String(data.Diastolic_Blood_Pressure) : '');
              setFormValue('totalCholesterol', data.Total_Cholesterol ? String(data.Total_Cholesterol) : '');
              setFormValue('hdl', data.HDL ? String(data.HDL) : '');
              setFormValue('ldl', data.LDL ? String(data.LDL) : '');

              // Use profile gender if available, otherwise fall back to risk data gender
              const finalGender = profileGender || (data.Gender ? data.Gender.toLowerCase() : '');
              console.log('Final gender being set:', finalGender);

              // Update local state for controlled components
              setRiskForm(prev => ({
                ...prev,
                age: data.Age ? String(data.Age) : '',
                height: data.Height ? String(data.Height) : '',
                weight: data.Weight ? String(data.Weight) : '',
                systolicBp: data.Systolic_Blood_Pressure ? String(data.Systolic_Blood_Pressure) : '',
                diastolicBp: data.Diastolic_Blood_Pressure ? String(data.Diastolic_Blood_Pressure) : '',
                totalCholesterol: data.Total_Cholesterol ? String(data.Total_Cholesterol) : '',
                hdl: data.HDL ? String(data.HDL) : '',
                ldl: data.LDL ? String(data.LDL) : '',
                sex: finalGender,
                on_diabetes_medication: !!data.Diabetes,
                on_hypertension_medication: !!data.Hypertension,
                smoker: data.Smoking ? data.Smoking.toLowerCase() : 'never',
                race: data.Race ? data.Race : 'African American',
                on_statin: !!data.Statin,
                on_aspirin: !!data.Aspirin
              }));

              // Set BMI results if needed
              setBmiValue(data.BMI_Score);
              setBmiResult(data.BMI_Result);
              prepareBMICHart(data.BMI_Score, data.BMI_Result);
              setChartSeries([((data.BMI_Score / MAX_BMI) * 100) || 0]); // Ensure chart is in sync
              setIsShownResults(true);
            } else {
              // If no risk analysis data, just set the gender from profile
              setRiskForm(prev => ({
                ...prev,
                sex: profileGender
              }));
            }
          });
      })
      .catch((error) => {
        console.error("Error fetching user data:", error);
      });
  }, [dispatch, token, selectedUserId, setFormValue, props.selectedFamilyMember]);

  // 1. Chart options for half-donut BMI gauge
  const MAX_BMI = 40; // or 45, depending on your design

  // Calculate BMI from form values
  const height = parseFloat(riskForm.height);
  const weight = parseFloat(riskForm.weight);
  const bmi = height && weight ? +(weight / ((height / 100) ** 2)).toFixed(2) : 0;

  // Chart percentage for the arc
  const bmiPercent = Math.min((bmi / MAX_BMI) * 100, 100);
  const bmiChartSeries = [bmiPercent];

  // Chart options
  const bmiChartOptions = {
    chart: {
      type: 'radialBar',
      offsetY: 0,
      sparkline: { enabled: true },
      height: 300,
      width: 300
    },
    plotOptions: {
      radialBar: {
        startAngle: -90,
        endAngle: 90,
        hollow: {
          margin: 0,
          size: '70%',
          background: 'transparent',
        },
        track: {
          background: '#eeeeee',
          strokeWidth: '100%',
          margin: 0,
          dropShadow: { enabled: false }
        },
        dataLabels: {
          name: { show: false },
          value: {
            offsetY: 20,
            fontSize: '24px',
            color: '#888',
            show: true,
            formatter: function (val) {
              if (val === 0) return ""; // Hide the value when BMI is zero
              const bmiValue = (val / 100) * MAX_BMI;
              return `${bmiValue.toFixed(2)} Kgs/m²`;
            }
          },
          total: { show: false }
        }
      }
    },
    fill: {
      type: 'gradient',
      gradient: {
        shade: 'light',
        type: 'horizontal',
        shadeIntensity: 0.5,
        gradientToColors: [themeColors.primaryHover],
        inverseColors: false,
        opacityFrom: 1,
        opacityTo: 1,
        stops: [0, 100]
      },
      colors: [themeColors.primary]
    },
    stroke: { lineCap: 'round' },
    labels: ['BMI'],
  };

  return (
    <div className="row align-items-start py-2">
      <div className="col-12 col-lg-6">
        <h2 className="mx-1 mb-2 text-center text-primary text-capitalize">{t('riskcalculatorbmi.title')}</h2>
        <div className="card m-2">
          <div className="card-body">
            <form className="validate-form" onSubmit={handleSubmit(calculateHealthRisk)}>
              <div className="row">
                <div className="col-12 col-md-4">
                  {renderInputField('age', 'riskcalculatorbmi.current-age', 'Enter Age', rowColors.row1)}
                </div>
                <div className="col-12 col-md-4">
                  {renderInputField('height', 'riskcalculatorbmi.height', 'Enter Height', rowColors.row1)}
                </div>
                <div className="col-12 col-md-4">
                  {renderInputField('weight', 'riskcalculatorbmi.weight', 'Enter Weight', rowColors.row1)}
                </div>
              </div>

              <div className="row">
                <div className="col-12 col-md-6">
                  {renderInputField('systolicBp', 'riskcalculatorbmi.systolic-bp', 'Enter Systolic BP', rowColors.row2)}
                </div>
                <div className="col-12 col-md-6">
                  {renderInputField('diastolicBp', 'riskcalculatorbmi.diastolic-bp', 'Enter Diastolic BP', rowColors.row2)}
                </div>
              </div>

              <div className="row">
                <div className="col-12 col-md-4">
                  {renderInputField('totalCholesterol', 'riskcalculatorbmi.cholesterol', 'Total Cholesterol', rowColors.row3)}
                </div>
                <div className="col-12 col-md-4">
                  {renderInputField('hdl', 'riskcalculatorbmi.hdl', 'Enter HDL', rowColors.row3)}
                </div>
                <div className="col-12 col-md-4">
                  {renderInputField('ldl', 'riskcalculatorbmi.ldl', 'Enter LDL', rowColors.row3)}
                </div>
              </div>

              <div className="row">
                <div className="col-12 col-md-6">
                  <div className="form-group ">
                    <label htmlFor="sex" className="bmiLabels">{t('riskcalculatorbmi.gender')}</label>
                    <div className="btn-group w-100 gap-0" role="group">
                      <button
                        type="button" id="gender"
                        className={`bmibtn btn  btn-outline-primary w-50 ${riskForm.sex === 'male' ? 'active' : ''}`}
                        onClick={() => handleToggle('sex', 'male')}
                      >
                        Male
                      </button>
                      <button
                        type="button"
                        className={`bmibtn btn btn-outline-primary w-50 ${riskForm.sex === 'female' ? 'active' : ''}`}
                        onClick={() => handleToggle('sex', 'female')}
                      >
                        Female
                      </button>
                    </div>
                  </div>
                </div>
                <div className="col-12 col-md-6">
                  <label htmlFor="on_diabetes_medication" className="bmiLabels">{t('riskcalculatorbmi.history-of-diabetes')}</label>
                  <div className="btn-group w-100 gap-0" role="group">
                    <button
                      type="button"
                      className={`bmibtn btn btn-outline-primary w-50 ${riskForm.on_diabetes_medication ? 'active' : ''}`}
                      onClick={() => handleToggle('on_diabetes_medication', true)}
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      className={`bmibtn btn btn-outline-primary w-50 ${!riskForm.on_diabetes_medication ? 'active' : ''}`}
                      onClick={() => handleToggle('on_diabetes_medication', false)}
                    >
                      No
                    </button>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-12 col-md-6">
                  <div className="form-group">
                    <label htmlFor="on_hypertension_medication" className="bmiLabels">{t('riskcalculatorbmi.hypertension-treatment')}</label>
                    <div className="btn-group w-100 gap-0" role="group">
                      <button
                        type="button"
                        className={`bmibtn btn btn-outline-primary w-50 ${riskForm.on_hypertension_medication ? 'active' : ''}`}
                        onClick={() => handleToggle('on_hypertension_medication', true)}
                      >
                        Yes
                      </button>
                      <button
                        type="button"
                        className={`bmibtn btn btn-outline-primary w-50 ${!riskForm.on_hypertension_medication ? 'active' : ''}`}
                        onClick={() => handleToggle('on_hypertension_medication', false)}
                      >
                        No
                      </button>
                    </div>

                  </div>
                </div>
                <div className="col-12 col-md-6">
                  <label htmlFor="smoker" className="bmiLabels">{t('riskcalculatorbmi.smoker')}</label>
                  <div className="btn-group w-100 gap-0" role="group">
                    <button
                      type="button"
                      className={`bmibtn btn btn-outline-primary w-33 ${riskForm.smoker === 'current' ? 'active' : ''}`}
                      onClick={() => handleToggle('smoker', 'current')}
                    >
                      Current
                    </button>
                    <button
                      type="button"
                      className={`bmibtn btn btn-outline-primary w-33 ${riskForm.smoker === 'former' ? 'active' : ''}`}
                      onClick={() => handleToggle('smoker', 'former')}
                    >
                      Former
                    </button>
                    <button
                      type="button"
                      className={`bmibtn btn btn-outline-primary w-33 ${riskForm.smoker === 'never' ? 'active' : ''}`}
                      onClick={() => handleToggle('smoker', 'never')}
                    >
                      Never
                    </button>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-12">
                  <div className="form-group">
                    <label htmlFor="race" className="bmiLabels">{t('riskcalculatorbmi.race')}</label>
                    <div className="btn-group w-100 gap-0" role="group">
                      <button
                        type="button"

                        className={`bmibtn btn btn-outline-primary w-33 ${riskForm.race === 'white' ? 'active' : ''}`}
                        onClick={() => handleToggle('race', 'white')}
                      >
                        White
                      </button>
                      <button
                        type="button"

                        className={`bmibtn btn btn-outline-primary w-33 ${riskForm.race === 'African American' ? 'active' : ''}`}
                        onClick={() => handleToggle('race', 'African American')}
                      >
                        African American
                      </button>
                      <button
                        type="button"

                        className={`bmibtn btn btn-outline-primary w-33 ${riskForm.race === 'other' ? 'active' : ''}`}
                        onClick={() => handleToggle('race', 'other')}
                      >
                        Other
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-12 col-md-6">
                  <label htmlFor="on_statin" className="bmiLabels">{t('riskcalculatorbmi.on-statin')}</label>
                  <div className="btn-group w-100 gap-0" role="group">
                    <button
                      type="button"
                      className={`bmibtn btn btn-outline-primary w-50 ${riskForm.on_statin ? 'active' : ''}`}
                      onClick={() => handleToggle('on_statin', true)}
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      className={`bmibtn btn btn-outline-primary w-50 ${!riskForm.on_statin ? 'active' : ''}`}
                      onClick={() => handleToggle('on_statin', false)}
                    >
                      No
                    </button>
                  </div>
                </div>
                <div className="col-12 col-md-6">
                  <label htmlFor="on_aspirin" className="bmiLabels">{t('riskcalculatorbmi.on-aspirin')}</label>
                  <div className="btn-group w-100 gap-0" role="group">
                    <button
                      type="button"

                      className={`bmibtn btn btn-outline-primary w-50 ${riskForm.on_aspirin ? 'active' : ''}`}
                      onClick={() => handleToggle('on_aspirin', true)}
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      className={`bmibtn btn btn-outline-primary w-50 ${!riskForm.on_aspirin ? 'active' : ''}`}
                      onClick={() => handleToggle('on_aspirin', false)}
                    >
                      No
                    </button>
                  </div>
                </div>
              </div>

              <div className="row justify-content-center mt-4">
                <div className="col-auto">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    onClick={handleCalculateRisk}
                    disabled={isLoading}
                    style={{
                      opacity: isLoading ? 0.6 : 1,
                      cursor: isLoading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Calculating...
                      </>
                    ) : (
                      'Calculate BMI'
                    )}
                  </button>
                </div>
                <div className="col-auto">
                  <button type="button" className="resetButton" style={{ borderRadius: 5, width: 100 }} onClick={resetForm}>
                    Reset
                  </button>
                </div>
              </div>
            </form>
          </div>
          <span className="ml-1 mb-3 ms-3 text-gray p-2" style={{ fontSize: '14px' }}>
            <b>
              <b>Disclaimer</b>:&nbsp;&nbsp;<i>Calculator validated for ages 20-79. May underestimate or overestimate risk for other age groups.
                Also, the calculator will not calculate risk accurately for people of South Asian ancestry who have an increased risk of atherosclerotic cardiovascular disease.
                This subpopulation may need additional risk stratification with tools such as calcium score. Please consult with your physician.</i>
            </b>
          </span>
        </div>
        {errorMessage && <div className="alert alert-danger mt-2" style={{ marginLeft: '0.5rem', marginRight: '0.5rem' }}>{errorMessage}</div>}
      </div>

      {/* Right column */}
      <div className="col-12 col-lg-6">
        <h2 className="mx-1 mb-2 text-center text-primary text-capitalize ">Result</h2>
        <div className="card shadow-sm mt-2 me-3" style={{ height: '550px', minHeight: '550px' }}>
          <div className="card-body p-4 lh-base">
            <div className="text-center mb-2">
              <h3 className="fw-bold text-uppercase mb-0">BMI</h3>
            </div>
            <div className="row">
              <div className="col-12 col-md-6">
                <div className="mb-3">
                  <span className="fw-bold">BMI Value: </span>
                  <span>
                    {isShownResults && bmiValue ? bmiValue.toFixed(2) : '0.00'}
                    {isShownResults && bmiValue > 0 && bmiValue < 18.5 && ' (Underweight)'}
                    {isShownResults && bmiValue >= 18.5 && bmiValue < 25 && ' (Normal)'}
                    {isShownResults && bmiValue >= 25 && bmiValue < 30 && ' (Overweight)'}
                    {isShownResults && bmiValue >= 30 && ' (Obese)'}
                  </span>
                </div>
                {isShownResults && bmiValue > 0 && (
                  <div className="bmi-chart-container d-flex justify-content-center" style={{ position: 'relative', height: 300 }}>
                    <Chart
                      className="bmi-chart"
                      options={bmiChartOptions}
                      series={chartSeries}
                      type="radialBar"
                      height={300}
                      width={300}
                    />
                  </div>
                )}
              </div>
              <div className="col-12 col-md-6">
                <div className="table-responsive fs-6 d-flex flex-column" style={{ height: '450px', minHeight: '450px', overflowY: 'hidden' }}>
                  <table className="table table-bordered mb-0 flex-grow-1" style={{ fontSize: '12px' }}>
                    <thead>
                      <tr className="bg-gray">
                        <th className="py-2 px-3 border-1 fw-bold text-center" style={{ backgroundColor: '#f8f9fa', fontSize: '14px', color: 'var(--pmr-text)' }}>Category</th>
                        <th className="py-2 px-3 border-1 fw-bold text-center" style={{ backgroundColor: '#f8f9fa', fontSize: '14px', color: 'var(--pmr-text)' }}>BMI Range</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bmiCategories.map((item, index) => (
                        <tr key={index} >
                          <td className="py-2 px-3 border-1 fw-bold text-center" style={{
                            backgroundColor: index % 2 === 0 ? '#fff' : '#f8f9fa', color: 'var(--pmr-text)'
                          }}>{item.category}</td>
                          <td className="py-2 px-3 border-1 fw-bold text-center" style={{
                            backgroundColor: index % 2 === 0 ? '#fff' : '#f8f9fa', color: 'var(--pmr-text)'
                          }}>{item.range}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        {isShownResults && (
          <div className="card shadow-sm mt-3">
            <div className="card-body p-4">
              {isLoading ? (
                <StethoscopeLoading message="Calculating your BMI..." />
              ) : (
                <div dangerouslySetInnerHTML={{ __html: htmlResponse }} />
              )}
            </div>
          </div>
        )}

        {isLoading && !isShownResults && (
          <div className="card shadow-sm mt-3">
            <div className="card-body p-4">
              <StethoscopeLoading message="Calculating your BMI..." />
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default RiskCalculator;


